// داده‌ی سربرگِ «تستِ دستی» در اپِ ادمین (مایگریشن‌های 0161–0166).
//
// برخلافِ opsRepo این‌ها RPC نیستند و مستقیم از جدول می‌خوانند: RLSِ همان
// جدول‌ها به ادمین همه‌ی ردیف‌ها را نشان می‌دهد و به تستر فقط ردیف‌های
// خودش، پس لایه‌ی امنیتی همان policyهاست. تنها جایی که RPC لازم شد افزودنِ
// تستر است، چون پیدا کردنِ حساب از روی ایمیل به auth.users نیاز دارد.
//
// ⚠ این جدول‌ها فقط روی دیتابیسِ develop پر می‌شوند — ادمینی که به آن وصل
// است admin.goout-1e4.pages.dev است، نه admin.whosup.ca.
import { supabase } from '../lib/supabase';
import {
  inviteEmail, inviteKey,
  type QaAssignment, type QaBugHandled, type QaBugJira, type QaDecision, type QaEvent, type QaInvite, type QaLink, type QaNoteReview, type QaScenario,
  type QaStateRow, type QaTester, type QaTicket, type QaTriageEffect,
} from '../lib/qaReport';

const rows = async <T>(q: PromiseLike<{ data: unknown; error: unknown }>): Promise<T> => {
  const { data, error } = await q;
  if (error) throw error;
  return data as T;
};

export const qaRepo = {
  catalog: () => rows<QaScenario[]>(supabase.from('qa_scenarios').select('*').order('sort')),

  /** تیکت‌ها (Jira و نام‌دار) و پلِ تیکت↔سناریو (0166). */
  tickets: () => rows<QaTicket[]>(supabase.from('qa_tickets').select('*').order('created_at')),
  // '*' and not a column list: `stage` (0185) comes along once the migration ran, nothing breaks before.
  links: () => rows<QaLink[]>(supabase.from('qa_ticket_scenarios').select('*')),

  /** تیکتِ نام‌دارِ تازه (Q-n)، با اسمی که ادمین می‌دهد؛ کلیدش را برمی‌گرداند. */
  createNamedTicket: (title: string) => rows<string>(supabase.rpc('admin_qa_create_named_ticket', { p_title: title })),

  /** دورِ تازه برای یک تیکتِ Jira با همان تسترها (مایگریشن 0171)؛ کلیدِ Q-n را برمی‌گرداند. */
  newRound: (jiraKey: string, title: string, ids: string[]) =>
    rows<string>(supabase.rpc('admin_qa_new_round', { p_jira: jiraKey, p_title: title, p_scenarios: ids })),

  /** تیکتِ Jira که ادمین دستی می‌سازد؛ اگر از قبل هست (مثلاً اتوپایلوت ساخته) دست نمی‌خورد. */
  ensureJiraTicket: (key: string, title: string) => rows<null>(supabase.from('qa_tickets')
    .upsert({ key, kind: 'jira', title: title.trim() || null, source: 'admin' }, { onConflict: 'key', ignoreDuplicates: true })),

  /**
   * عوض کردنِ اسمِ تیکت (مثلاً یک اساینِ نام‌دار). title_en هم پاک می‌شود تا صفحه‌ی
   * تستر در انگلیسی هم اسمِ تازه را نشان دهد، نه ترجمه‌ی اسمِ قبلی را.
   */
  renameTicket: (key: string, title: string) => rows<null>(supabase.from('qa_tickets')
    .update({ title: title.trim() || null, title_en: null }).eq('key', key)),

  deleteTicket: (key: string) => rows<null>(supabase.from('qa_tickets').delete().eq('key', key)),

  /** گذاشتنِ سناریوها در یک تیکت با نقشِ «اساین‌شده»؛ سناریویی که از قبل در آن است نقشش را نگه می‌دارد. */
  addToTicket: (ids: string[], key: string) => rows<null>(supabase.from('qa_ticket_scenarios').upsert(
    ids.map((scenario_id, i) => ({ ticket_key: key, scenario_id, role: 'assigned', sort: 10_000 + i })),
    { onConflict: 'ticket_key,scenario_id', ignoreDuplicates: true })),

  removeFromTicket: (ids: string[], key: string) =>
    rows<null>(supabase.from('qa_ticket_scenarios').delete().eq('ticket_key', key).in('scenario_id', ids)),

  /** اساینِ تیکت‌ها به تسترها و دعوت‌شده‌ها با هم؛ دعوت‌شده با `invite:<email>` (0163). */
  assignments: async (): Promise<QaAssignment[]> => {
    const [members, invited] = await Promise.all([
      rows<QaAssignment[]>(supabase.from('qa_assignments').select('ticket_key, user_id, assigned_at')),
      rows<{ ticket_key: string; email: string; assigned_at: string }[]>(
        supabase.from('qa_invite_assignments').select('ticket_key, email, assigned_at')),
    ]);
    return [...members, ...invited.map((x) => ({ ticket_key: x.ticket_key, user_id: inviteKey(x.email), assigned_at: x.assigned_at }))];
  },

  /** کسانی که دوری را برداشتند و پس دادند (0189). پیش از آن مایگریشن جدول نیست: هیچ‌کس. */
  pastOwners: async (): Promise<QaAssignment[]> => {
    const { data, error } = await supabase.from('qa_round_owners_past').select('ticket_key, user_id, released_at');
    return error ? [] : (data ?? []).map((x) => ({ ticket_key: x.ticket_key, user_id: x.user_id, assigned_at: x.released_at }));
  },

  /** اساینِ یک تیکت به یک تستر یا یک دعوت‌شده. تکراری نادیده گرفته می‌شود. */
  assign: (key: string, who: string) => {
    const email = inviteEmail(who);
    return rows<null>(email
      ? supabase.from('qa_invite_assignments').upsert({ ticket_key: key, email }, { onConflict: 'ticket_key,email', ignoreDuplicates: true })
      : supabase.from('qa_assignments').upsert({ ticket_key: key, user_id: who }, { onConflict: 'ticket_key,user_id', ignoreDuplicates: true }));
  },

  unassign: (key: string, who: string) => {
    const email = inviteEmail(who);
    return rows<null>(email
      ? supabase.from('qa_invite_assignments').delete().eq('email', email).eq('ticket_key', key)
      : supabase.from('qa_assignments').delete().eq('user_id', who).eq('ticket_key', key));
  },

  /**
   * دورِ آزاد (0183): همه‌ی تسترها می‌بینند و اولین کسی که «برمی‌دارم» را بزند صاحبش می‌شود.
   * فقط دورِ بی‌اساین را آزاد کن — هر اساینی دوباره می‌بنددش (trg_qa_assign_closes).
   */
  setOpen: (key: string, open: boolean) => rows<null>(supabase.from('qa_tickets').update({ open }).eq('key', key)),

  /** پیش‌فرض: یک تستر یا یک دعوت‌شده (`invite:<email>`)، یا null یعنی هیچ‌کس (0163). */
  setDefault: (who: string | null) => {
    const email = who ? inviteEmail(who) : null;
    return rows<null>(supabase.rpc('admin_qa_set_default', {
      p_user_id: who && !email ? who : null, p_email: email,
    }));
  },

  testers: () => rows<QaTester[]>(supabase.from('qa_testers').select('*').order('added_at')),

  /** شناسه‌ی حساب، یا null یعنی ایمیل هنوز حساب ندارد و دعوت شد (0162). */
  addTester: (email: string, name: string) =>
    rows<string | null>(supabase.rpc('admin_qa_add_tester', { p_email: email, p_name: name || null })),

  /** ایمیل‌های دعوت‌شده‌ای که هنوز حساب نساخته یا صفحه‌ی تست را باز نکرده‌اند. */
  invites: () => rows<QaInvite[]>(
    supabase.from('qa_tester_invites').select('*').order('invited_at')),

  removeInvite: (email: string) => rows<null>(supabase.from('qa_tester_invites').delete().eq('email', email)),

  /** نامی که ادمین به تستر (یا ایمیلِ دعوت‌شده) می‌دهد؛ خالی یعنی بی‌نام. */
  setTesterName: (userId: string, name: string) =>
    rows<null>(supabase.from('qa_testers').update({ name: name.trim() || null }).eq('user_id', userId)),
  setTesterDevices: (userId: string, ids: string[]) =>
    rows<null>(supabase.from('qa_testers').update({ device_ids: ids }).eq('user_id', userId)),
  setInviteName: (email: string, name: string) =>
    rows<null>(supabase.from('qa_tester_invites').update({ name: name.trim() || null }).eq('email', email)),

  setTesterActive: (userId: string, active: boolean) =>
    rows<null>(supabase.from('qa_testers').update({ active }).eq('user_id', userId)),

  /** تیکتِ Jiraی هر باگ و وضعیتش (0167/0168). */
  bugJira: () => rows<QaBugJira[]>(supabase.from('qa_bug_jira').select('*')),
  bugHandled: () => rows<QaBugHandled[]>(supabase.from('qa_bug_handled').select('user_id, note_id, handled_at')),
  setBugHandled: (userId: string, noteId: string, on: boolean) => on
    ? rows<null>(supabase.from('qa_bug_handled').upsert({ user_id: userId, note_id: noteId }, { onConflict: 'user_id,note_id', ignoreDuplicates: true }))
    : rows<null>(supabase.from('qa_bug_handled').delete().eq('user_id', userId).eq('note_id', noteId)),
  /** تصمیم‌های مدیر روی «نظرِ تستر» (0186). پیش از آن مایگریشن جدول نیست → هیچ. */
  noteReviews: async (): Promise<QaNoteReview[]> => {
    const { data, error } = await supabase.from('qa_note_reviews').select('user_id, note_id, decision, decided_at');
    return error ? [] : (data as QaNoteReview[]);
  },
  /**
   * اثرِ تصمیم‌های مدیر روی «پرونده‌ی بررسی» (WHOSUP-382، 0192) — برای حکمِ دورها (ticketTable).
   * select *: پیش از مایگریشن جدول نیست → هیچ، مثلِ قبل.
   */
  triageEffects: async (): Promise<QaTriageEffect[]> => {
    const { data, error } = await supabase.from('qa_triage_cases').select('*').not('decided_at', 'is', null);
    return error ? [] : (data as QaTriageEffect[]);
  },
  /** «تأیید» یا «فقط یادداشت»؛ qa_review_note حکمِ Jira و تیکتِ 🧪 را هم همان لحظه دوباره می‌سنجد. */
  reviewNote: (userId: string, noteId: string, decision: QaDecision) =>
    rows<string>(supabase.rpc('qa_review_note', { p_user: userId, p_note: noteId, p_decision: decision })),
  /** از qa-jira می‌خواهد وضعیتِ تیکت‌های باگ را از Jira بخواند؛ جواب چند ثانیه بعد در qa_bug_jira است. */
  refreshBugStatus: () => rows<null>(supabase.rpc('admin_qa_bug_status_refresh')),

  states: () => rows<QaStateRow[]>(supabase.from('qa_state').select('user_id, state, updated_at')),

  /**
   * فقط رویدادهای ساعت، پایان و «کنار گذاشتنِ» سناریو؛ تپشِ هر ۳۰ ثانیه (beat) و بقیه لازم
   * نیستند و حجمشان زیاد است.
   */
  events: async (): Promise<QaEvent[]> => {
    const out: QaEvent[] = [];
    for (let from = 0; ; from += 1000) {
      const page = await rows<QaEvent[]>(supabase.from('qa_events').select('user_id, n, e')
        .in('e->>type', ['start', 'resume', 'pause', 'finish', 'discard']).order('user_id').order('n').range(from, from + 999));
      out.push(...page);
      if (page.length < 1000) return out;
    }
  },

  /**
   * ورودی‌های «ساعت، دو روش» (0176): همه‌ی رویدادها با beat (برای خواب و
   * بی‌کاری)، دقیقه‌های فعالیت در اپ/سایت، و سؤال‌های بات. فقط ادمین.
   */
  clockInputs: async () => {
    const all = async <T>(q: (from: number) => PromiseLike<{ data: unknown; error: unknown }>) => {
      const out: T[] = [];
      for (let from = 0; ; from += 1000) {
        const page = await rows<T[]>(q(from));
        out.push(...page);
        if (page.length < 1000) return out;
      }
    };
    const [events, activity, turns] = await Promise.all([
      all<QaEvent>((f) => supabase.from('qa_events').select('user_id, n, e').order('user_id').order('n').range(f, f + 999)),
      all<{ user_id: string; minute: string }>((f) => supabase.from('qa_app_activity').select('user_id, minute')
        .order('user_id').order('minute').order('env').range(f, f + 999)),
      all<{ user_id: string; asked_at: string; answered_at: string; ticket_key: string | null; sid: string | null }>((f) =>
        supabase.from('qa_bot_turns').select('user_id, asked_at, answered_at, ticket_key, sid').order('user_id').order('answered_at').range(f, f + 999)),
    ]);
    return { events, activity, turns };
  },

  photo: async (userId: string, id: string) => {
    const r = await rows<{ data: string } | null>(
      supabase.from('qa_photos').select('data').eq('user_id', userId).eq('id', id).maybeSingle());
    return r?.data ?? null;
  },
};
